import { useEffect, useMemo, useState } from 'react';

import useOfferDesign from 'api/useOfferDesign';
import { OfferDesignTypes } from 'views/OfferDesign/OfferDesign.types';
import { resolveOfferDesignType } from 'views/OfferDesign/OfferDesign.utils';

export interface IOfferDesignOption {
  value: string;
  label: string;
  externalId: string;
}

interface IUseOfferDesignOptionsProps {
  publisherId: string;
  offerDesignType: OfferDesignTypes;
  defaultExternalId?: string;
  offerId?: string;
  requiredOfferUiSubType?: string;
}

interface IUseOfferDesignOptionsReturn {
  offerDesignOptions: IOfferDesignOption[];
  defaultOfferDesignId: string;
  getOfferDesign: ReturnType<typeof useOfferDesign>['getOfferDesign'];
  getOfferDesigns: ReturnType<typeof useOfferDesign>['getOfferDesigns'];
  currentOfferDesign: string;
  setCurrentOfferDesign: (id: string) => void;
}

/**
 * Hook that centralizes offer design fetching, filtering, option mapping,
 * auto-refetch on design change, and default design selection for new offers.
 *
 * @example
 * const { offerDesignOptions, getOfferDesign, setCurrentOfferDesign } = useOfferDesignOptions({
 *   publisherId,
 *   offerDesignType: OfferDesignTypes.Bundle,
 *   offerId
 * });
 */
export const useOfferDesignOptions = ({
  publisherId,
  offerDesignType,
  defaultExternalId,
  offerId,
  requiredOfferUiSubType
}: IUseOfferDesignOptionsProps): IUseOfferDesignOptionsReturn => {
  const [currentOfferDesign, setCurrentOfferDesign] = useState('');
  const { getOfferDesign, getOfferDesigns } = useOfferDesign(publisherId, currentOfferDesign);

  const offerDesignOptions: IOfferDesignOption[] = useMemo((): IOfferDesignOption[] => {
    if (!getOfferDesigns.data?.result) {
      return [];
    }

    return getOfferDesigns.data.result
      .filter(
        (oi: {
          offerUiType?: string;
          offerUiSubType?: string;
        }): boolean =>
          requiredOfferUiSubType
            ? oi.offerUiSubType === requiredOfferUiSubType
            : resolveOfferDesignType(oi.offerUiType || '', oi.offerUiSubType) ===
            offerDesignType
      )
      .map(
        (offerUI: {
          _id: string;
          name: string;
          externalId?: string;
        }): IOfferDesignOption => ({
          value: offerUI._id,
          label: offerUI.name,
          externalId: offerUI.externalId || ''
        })
      );
  }, [getOfferDesigns.data?.result, offerDesignType, requiredOfferUiSubType]);

  const defaultOfferDesignId: string = useMemo((): string => {
    if (!offerDesignOptions.length) return '';

    if (defaultExternalId) {
      const match = offerDesignOptions.find(
        (option: IOfferDesignOption): boolean =>
          option.externalId === defaultExternalId
      );
      if (match) return match.value;
    }

    // Fall back to the first available option
    return offerDesignOptions[0].value;
  }, [offerDesignOptions, defaultExternalId]);

  // Set default design when creating a new offer (no offerId)
  useEffect(() => {
    if (!offerId && defaultOfferDesignId) {
      setCurrentOfferDesign(defaultOfferDesignId);
    }
  }, [defaultOfferDesignId, offerId]);

  return {
    offerDesignOptions,
    defaultOfferDesignId,
    getOfferDesign,
    getOfferDesigns,
    currentOfferDesign,
    setCurrentOfferDesign
  };
};
